import { FC, FormEvent } from "react";
import { FieldError, useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useParams } from "react-router-dom";

import Button from "@/components/Button";
import { FormLayout, Input, Label, Larger, Textarea } from "@/components/Forms";
import { Modal } from "@/components/Modal";
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from "@/components/SidePanel";
import useAlerts from "@/hooks/useAlerts";
import { handleFormErrors } from "@/utils/helpers";
import { api } from "@/utils/api";
import { ChangelogChannelResourse, ChangelogResource } from "@/types/resources";
import ValidationMessage from "@/components/Forms/ValidationMessage";
import StrategyChannelsField from "./StrategyChannelsField";
import Datepicker from "@/components/Datepicker";

export type FormDataT = {
  name: string;
  event_time: string;
  channels: ChangelogChannelResourse[];
  notes: string;
};

interface Props {
  onClose: () => void;
  changelog?: ChangelogResource;
  queryKey: Array<string | object>;
}

const StrategyFormModal: FC<Props> = ({ onClose, changelog, queryKey }) => {
  const queryClient = useQueryClient();

  const { teamId } = useParams();

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    clearErrors,
    control,
    formState: { errors, isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: {
      name: changelog?.name ?? "",
      event_time: changelog?.eventTime
        ? DateTime.fromISO(changelog.eventTime).toFormat("yyyy-MM-dd'T'HH:mm")
        : "",
      channels: changelog?.channels ?? [],
      notes: changelog?.notes ?? "",
    },
  });

  const alerts = useAlerts();

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async ({ event_time, channels, ...data }) => {
      try {
        const body = {
          ...data,
          channels: channels.map((c) => c.id),
          event_time,
          ...(teamId !== "all" && { team_id: teamId }),
        };

        if (changelog) {
          await api.put(`/v1/changelogs/${changelog.id}`, body);
        } else {
          await api.post("/v1/changelogs", body);
        }

        queryClient.refetchQueries(queryKey, { exact: true });
        alerts.success(
          `Event was successfully ${!!changelog ? "updated" : "created"}`
        );
        onClose();
      } catch (err) {
        handleFormErrors(err, setError, alerts);
      }
    })(e);
  };

  return (
    <Modal>
      <SidePanel heading="Add event" onClose={onClose}>
        <SidePanelBody autoComplete="off" onSubmit={onSubmit}>
          <SidePanelContent>
            <FormLayout>
              <Larger>
                <Label htmlFor="eventInput">Event</Label>
                <Input
                  id="eventInput"
                  type="text"
                  defaultValue={changelog?.name}
                  placeholder="Add event"
                  $hasError={!!errors.name}
                  {...register("name")}
                />
                {!!errors.name && (
                  <ValidationMessage>{errors.name.message}</ValidationMessage>
                )}
              </Larger>
              <Larger>
                <Label htmlFor="channelInput">Channel</Label>
                <StrategyChannelsField
                  $hasError={!!errors.channels}
                  control={control}
                  setValue={setValue}
                />
                {!!errors.channels && (
                  <ValidationMessage>
                    {(errors.channels as unknown as FieldError).message}
                  </ValidationMessage>
                )}
              </Larger>
              <Larger>
                <Label htmlFor="dateInput">Date</Label>
                <Datepicker<FormDataT> name="event_time" control={control} />
                {!!errors.event_time && (
                  <ValidationMessage>
                    {errors.event_time.message}
                  </ValidationMessage>
                )}
              </Larger>
              <Larger>
                <Label htmlFor="noteInput">Note</Label>
                <Textarea
                  id="noteInput"
                  placeholder="Add note"
                  {...register("notes")}
                />
              </Larger>
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button disabled={isSubmitting} loading={isSubmitting}>
              {!!!changelog ? "Add" : "Update"}
            </Button>
            <Button color="secondary" onClick={onClose} type="button">
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default StrategyFormModal;
